import {Injectable} from '@angular/core';
import {ActivatedRouteSnapshot, CanActivate, CanActivateChild, Router, RouterStateSnapshot, UrlTree} from '@angular/router';
import {LockoutService} from 'auth-commons';
import {SmagApiService} from './api/smag-api.service';
import {ControlPanelUserType, getControlPanelUserType} from './shared/ec-economics';
import {Observable, of} from 'rxjs';
import {HierarchyEntity, HierarchyService} from './shared/hierarchy.service';
import {catchError, first, switchMap} from 'rxjs/operators';
import {EntityNetworkType} from 'smag-commons/dto/api';
import {EntityType} from 'smag-commons/cfg/hierarchy';

@Injectable()
export class EconomicsGuard implements CanActivate, CanActivateChild {
    constructor(private readonly router: Router,
                private readonly lockout: LockoutService,
                private readonly apiService: SmagApiService,
                private readonly hierarchyService: HierarchyService) {
    }

    async canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean | UrlTree> {
        try {
            const subject = await this.apiService.getSubjectInfo().toPromise();
            const segments = this.router.parseUrl(state.url).root.children.primary.segments.map(segment => segment.path);

            return this.hierarchyService.selection.pipe(
                first(),
                switchMap(resource => ((!resource || resource.type === EntityType.ENERGY_COMMUNITY) ?
                    of(resource) : // either no resource or an energy community is selected
                    this.getResourceCommunity(resource.id))))
                .toPromise()
                .then(ecResource => {
                    // when the menu icon is clicked the user navigates to '/c/ec/economics'
                    // and this guard will navigate to 'c/ec/economics/$child'.
                    // when this happens this guard gets re-triggered, to prevent an infinite loop
                    // check that the RouterStateSnapshot url is equal to 'c/ec/economics/$child'
                    switch (getControlPanelUserType(subject)) {
                        case ControlPanelUserType.SUPERVISOR: {
                            const defaultChild = 'aggregated';
                            const children = [defaultChild, 'redistributed', 'subjects-summary', 'totals-summary', 'plants', 'market'];
                            return (this.isParentEconomics(segments) && children.includes(segments[3])) || this.gotoEconomicsDefault(defaultChild, ecResource.id);
                        }
                        case ControlPanelUserType.THIRD_PARTY_PRODUCER: {
                            const defaultChild = 'totals-plants';
                            const children = [defaultChild, 'member-plants', 'market'];
                            return (this.isParentEconomics(segments) && children.includes(segments[3])) || this.gotoEconomicsDefault(defaultChild, ecResource.id);
                        }
                        case ControlPanelUserType.ADVANCED: {
                            const defaultChild = 'totals-community';
                            const children = [defaultChild, 'member-pods', 'market'];
                            return (this.isParentEconomics(segments) && children.includes(segments[3])) || this.gotoEconomicsDefault(defaultChild, ecResource.id);
                        }
                        default:
                            return this.gotoCommunityDashboard(subject.subject.network_id);
                    }
                });
        } catch (err) {
            console.error(err);
            this.exit();
            return false;
        }
    }

    canActivateChild(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean | UrlTree> {
        return this.canActivate(route, state);
    }

    private exit() {
        this.lockout.lockout();
    }

    private gotoCommunityDashboard(communityId: string): UrlTree {
        return this.router.createUrlTree(['/c', 'ec', 'dashboard'],
            {queryParams: {id: communityId}, queryParamsHandling: 'merge'});
    }

    private gotoEconomicsDefault(child: string, parentId: string): UrlTree | boolean {
        return this.router.createUrlTree(['/c', 'ec', 'economics', child], {
            queryParams: {
                id: parentId
            },
            queryParamsHandling: 'merge'
        });
    }

    private isParentEconomics(segments: string[]) {
        return segments[0] === 'c' && segments[1] === 'ec' && segments[2] === 'economics';
    }

    private getResourceCommunity(resourceId: string): Observable<HierarchyEntity | null> {
        return this.apiService.getNetworkOfResource(resourceId).pipe(
            switchMap(networkInfo => networkInfo?.type === EntityNetworkType.ENERGY_COMMUNITY ?
                this.hierarchyService.findEntityById(networkInfo.network_id).pipe(first()) :
                of(null)),
            catchError(() => of(null))
        );
    }
}
